import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Activity, Users, Clock, Zap, TrendingUp, TrendingDown, AlertTriangle, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


function StatCard({ icon: Icon, label, value, sub, color = 'text-primary' }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${color} opacity-60`} />
        <span className="text-[9px] uppercase tracking-widest text-muted-foreground/50">{label}</span>
      </div>
      <p className={`text-xl font-heading font-bold ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground/40 mt-0.5">{sub}</p>}
    </div>
  );
}

function GroupBar({ group, count, maxCount, avgMs, trend }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const isUp = trend > 0;
  const isDown = trend < 0;

  return (
    <div className="flex items-center gap-3" data-testid={`group-${group}`}>
      <div className="w-24 text-[10px] text-muted-foreground truncate text-right flex-shrink-0">{group}</div>
      <div className="flex-1 relative">
        <div className="h-6 bg-white/[0.02] rounded-lg overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="h-full bg-gradient-to-r from-primary/30 to-primary/10 rounded-lg"
          />
        </div>
        <div className="absolute inset-0 flex items-center px-2 justify-between">
          <span className="text-[10px] font-bold text-white/80">{count.toLocaleString()}</span>
          <span className="text-[9px] text-muted-foreground/50">{avgMs}ms</span>
        </div>
      </div>
      <div className="w-14 flex-shrink-0 text-right">
        {trend !== 0 && (
          <span className={`text-[9px] inline-flex items-center gap-0.5 ${isUp ? 'text-green-400' : isDown ? 'text-red-400' : 'text-muted-foreground/40'}`}>
            {isUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
}

function EndpointRow({ endpoint }) {
  const errorColor = endpoint.error_rate > 5 ? 'text-red-400' : endpoint.error_rate > 0 ? 'text-yellow-400' : 'text-green-400';
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-white/[0.03] last:border-0" data-testid={`endpoint-${endpoint.method}-${endpoint.path.replace(/\//g, '-')}`}>
      <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${endpoint.method === 'GET' ? 'bg-green-400/10 text-green-400' : endpoint.method === 'POST' ? 'bg-blue-400/10 text-blue-400' : endpoint.method === 'PUT' ? 'bg-yellow-400/10 text-yellow-400' : 'bg-red-400/10 text-red-400'}`}>
        {endpoint.method}
      </span>
      <span className="text-[10px] text-muted-foreground flex-1 truncate font-mono">{endpoint.path}</span>
      <span className="text-[10px] text-white/70 w-12 text-right">{endpoint.count}</span>
      <span className="text-[9px] text-muted-foreground/50 w-14 text-right">{endpoint.avg_ms}ms</span>
      <span className={`text-[9px] w-10 text-right ${errorColor}`}>{endpoint.error_rate}%</span>
    </div>
  );
}

function TimelineChart({ data }) {
  if (!data || data.length === 0) return <p className="text-[10px] text-muted-foreground/40 py-4 text-center">no data yet</p>;

  const max = Math.max(...data.map(d => d.requests), 1);
  const barCount = data.length;
  const showEvery = barCount > 24 ? Math.ceil(barCount / 12) : 1;

  return (
    <div className="flex items-end gap-px h-28" data-testid="timeline-chart">
      {data.map((d, i) => {
        const h = (d.requests / max) * 100;
        const hasErrors = d.errors > 0;
        return (
          <div key={i} className="flex-1 flex flex-col items-center group relative min-w-0">
            <div className="w-full relative" style={{ height: '100px' }}>
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ duration: 0.4, delay: i * 0.01 }}
                className={`absolute bottom-0 w-full rounded-t-sm ${hasErrors ? 'bg-gradient-to-t from-red-500/30 to-primary/20' : 'bg-gradient-to-t from-primary/30 to-primary/10'}`}
              />
            </div>
            {i % showEvery === 0 && (
              <span className="text-[7px] text-muted-foreground/30 mt-1 truncate w-full text-center">
                {d.time?.split('T')[1]?.slice(0, 5) || d.time?.slice(5) || ''}
              </span>
            )}
            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-bg-paper border border-white/10 rounded-lg px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
              <p className="text-[9px] text-white">{d.requests} req</p>
              <p className="text-[8px] text-muted-foreground/50">{d.avg_ms}ms avg</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ApiAnalytics() {
  const [overview, setOverview] = useState(null);
  const [groups, setGroups] = useState(null);
  const [endpoints, setEndpoints] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState('overview');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, grRes, epRes, tlRes] = await Promise.all([
        authFetch(`${API}/api/analytics/overview?days=${days}`),
        authFetch(`${API}/api/analytics/groups?days=${days}`),
        authFetch(`${API}/api/analytics/endpoints?days=${days}`),
        authFetch(`${API}/api/analytics/timeline?days=${days}&bucket=${days <= 1 ? 'hour' : 'hour'}`),
      ]);
      if (ovRes.ok) setOverview(await ovRes.json());
      if (grRes.ok) setGroups(await grRes.json());
      if (epRes.ok) setEndpoints(await epRes.json());
      if (tlRes.ok) setTimeline(await tlRes.json());
    } catch {} finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div data-testid="api-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary/60" />
          <p className="text-xs text-muted-foreground">API usage & performance</p>
        </div>
        <div className="flex items-center gap-2">
          {[1, 7, 30].map(d => (
            <button
              key={d}
              data-testid={`period-${d}d`}
              onClick={() => setDays(d)}
              className={`text-[9px] px-2.5 py-1 rounded-full transition-all ${days === d ? 'bg-primary/15 text-primary border border-primary/30' : 'bg-white/5 text-muted-foreground border border-white/5 hover:border-white/10'}`}
            >
              {d === 1 ? '24h' : `${d}d`}
            </button>
          ))}
          <button onClick={fetchAll} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" data-testid="refresh-analytics">
            <RefreshCw className={`w-3.5 h-3.5 text-muted-foreground/50 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && !overview ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
          </div>
        </div>
      ) : (
        <>
          {overview && (
            <div className="grid grid-cols-2 gap-2 mb-4">
              <StatCard icon={Activity} label="Total Requests" value={overview.total_requests?.toLocaleString() || '0'} sub={`${overview.today_requests?.toLocaleString() || 0} today`} />
              <StatCard icon={Users} label="Active Users" value={overview.active_users || 0} color="text-secondary" />
              <StatCard icon={Clock} label="Avg Response" value={`${overview.avg_response_ms || 0}ms`} sub={`p95: ${overview.p95_response_ms || 0}ms`} color="text-green-400" />
              <StatCard icon={AlertTriangle} label="Rate Limited" value={overview.rate_limited_count || 0} color={overview.rate_limited_count > 0 ? 'text-yellow-400' : 'text-muted-foreground'} />
            </div>
          )}

          <div className="flex gap-1.5 mb-4">
            {[
              { key: 'overview', label: 'Traffic', icon: Activity },
              { key: 'groups', label: 'Routes', icon: Zap },
              { key: 'endpoints', label: 'Endpoints', icon: BarChart3 },
            ].map(t => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`text-[10px] px-3 py-1.5 rounded-full inline-flex items-center gap-1 transition-all ${tab === t.key ? 'bg-primary/15 text-primary border border-primary/30' : 'bg-white/5 text-muted-foreground border border-white/5 hover:border-white/10'}`}
              >
                <t.icon className="w-3 h-3" /> {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50">Request Volume</p>
                  <p className="text-[9px] text-muted-foreground/30">{timeline?.data?.length || 0} data points</p>
                </div>
                <TimelineChart data={timeline?.data || []} />
              </div>
              {overview?.by_group && overview.by_group.length > 0 && (
                <div className="mt-3 bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 mb-3">By Feature</p>
                  <div className="space-y-2">
                    {overview.by_group.slice(0, 8).map(g => (
                      <div key={g.group} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{g.group}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-white/70">{g.count.toLocaleString()}</span>
                          <span className="text-[9px] text-muted-foreground/40 w-14 text-right">{g.avg_ms}ms</span>
                          {g.errors > 0 && <span className="text-[9px] text-red-400">{g.errors} err</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {tab === 'groups' && groups && (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50">Route Groups</p>
                  <p className="text-[9px] text-muted-foreground/30">trend vs prior half</p>
                </div>
                <div className="space-y-2.5">
                  {(groups.groups || []).map(g => (
                    <GroupBar
                      key={g.group}
                      group={g.group}
                      count={g.count}
                      maxCount={Math.max(...(groups.groups || []).map(x => x.count), 1)}
                      avgMs={g.avg_ms}
                      trend={g.trend_pct}
                    />
                  ))}
                </div>
                {(!groups.groups || groups.groups.length === 0) && (
                  <p className="text-[10px] text-muted-foreground/40 py-4 text-center">no data yet</p>
                )}
              </div>
            </motion.div>
          )}

          {tab === 'endpoints' && endpoints && (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 mb-3">Top Endpoints</p>
                <div className="flex items-center gap-2 pb-1.5 mb-1.5 border-b border-white/5">
                  <span className="text-[8px] text-muted-foreground/30 w-8">Method</span>
                  <span className="text-[8px] text-muted-foreground/30 flex-1">Path</span>
                  <span className="text-[8px] text-muted-foreground/30 w-12 text-right">Calls</span>
                  <span className="text-[8px] text-muted-foreground/30 w-14 text-right">Avg</span>
                  <span className="text-[8px] text-muted-foreground/30 w-10 text-right">Err%</span>
                </div>
                {(endpoints.endpoints || []).slice(0, 20).map((ep, i) => (
                  <EndpointRow key={i} endpoint={ep} />
                ))}
                {(!endpoints.endpoints || endpoints.endpoints.length === 0) && (
                  <p className="text-[10px] text-muted-foreground/40 py-4 text-center">no endpoint data yet</p>
                )}
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
